import { axiosInstance as axios } from '3-foundation/ContentApi/axiosInstance';
import { CONTENT_API_URL, CDA_KEY } from '../_constants';
import Application from '3-foundation/Application/ApplicationEntity';
import logger from '3-foundation/Logging/logger';
import { AxiosRequestConfig } from 'axios';
import { AxiosError } from '3-foundation/Error/axiosError';
import { ContentApiError } from '3-foundation/Error/contentApiError';

export interface RequestInterface {
	url: string;
	urlParams?: {
		[key: string]: string;
	};
	siteId?: string;
	preview?: boolean;
}

export const queryCDA = async <TResponse = unknown>({ url, urlParams, siteId, preview }: RequestInterface): Promise<TResponse> => {
	const locale = Application.locale?.baseName;

	try {
		const requestOptions: AxiosRequestConfig = {
			url: encodeURI(`${CONTENT_API_URL}${url}`),
			params: urlParams,
			method: 'GET',
			headers: {
				'Accept-language': locale,
				Preview: preview ? true : false,
				'Api-key': CDA_KEY,
			},
		};
		if (siteId) requestOptions.headers['Start-item'] = siteId;

		const response = await axios(requestOptions);
		const { data } = response ?? {};
		if (data?.errors) throw data?.errors;
		return data;
	} catch (error) {
		if (axios.isAxiosError(error)) throw new AxiosError(error, { cause: error });

		const message = error?.response?.data?.message ?? error;
		const statusCode = error?.response?.status || 500;

		if (statusCode === 301 || statusCode === 302 || statusCode === 307 || statusCode === 308) {
			const statusName = statusCode
				? statusCode === 301
					? 'Moved Permanently'
					: statusCode === 302
						? 'Found'
						: statusCode === 307
							? 'Temporary Redirect'
							: statusCode === 308
								? 'Permanent Redirect'
								: ''
				: 'statusCode not defined';
			logger.info(`[${statusCode}] Redirection: ${statusName} \n${error?.response?.headers?.location}`);
			throw new ContentApiError({ name: 'Redirection', query: url, error, cause: error });
		}

		if (statusCode === 404) {
			logger.error(`[${statusCode}] Client error \nPage not found`, { path: url });
			throw new ContentApiError({ name: 'PageNotFound', query: url, error, cause: error });
		}

		if (statusCode === 401 || statusCode === 403) {
			// "401 Unauthorized" really means "unauthenticated"
			// "403 Forbidden" really means "unauthorized"
			logger.error(`[${statusCode}] Client error \n${error?.response?.data?.data}`);
			throw new ContentApiError({ name: 'Auth error', query: url, error: error?.response?.data?.data, cause: error });
		}

		logger.error(`[${statusCode}] Undetected API error in queryCDA() \n${message}`);
		throw new ContentApiError({ name: 'Undetected API error', query: url, error, cause: error });
	}
};
